import { useEffect, useMemo, useState } from 'react';
import { InputNumber, Select, Space, Typography } from 'antd';

import ToggleRow from '../common/ToggleRow';
import { LOCK_UNIT_OPTIONS, lockMinutesFrom, lockQtyMax, splitLockMinutes } from '../../utils/recordPolicy';

/**
 * Whether this card's items are working notes or **records**: entries that
 * freeze once made and are corrected rather than edited.
 *
 * A time card after payroll close, an issued invoice, an inspection
 * sign-off, a journal entry, an incident report, a medication
 * administration — all the same property, which is why it is a card switch
 * and not a feature of any one of them.
 *
 * Two things the panel says out loud, because both surprise people
 * otherwise: a lock does not stop the system from recomputing values it owns
 * (driven fields and automations still write), and a card that locks its
 * entries also stops casual deletes — a lock a DELETE walks around is
 * theatre.
 */

const { Text } = Typography;

const AFTER_OPTIONS = [
	{ value: 'create', label: 'As soon as it is saved' },
	{ value: 'minutes', label: 'After a grace period' },
];

const DELETE_OPTIONS = [
	{ value: 'manage-with-reason', label: 'A manager, with a reason' },
	{ value: 'never', label: 'Never — corrections only' },
	{ value: 'allowed', label: 'Anyone who can edit' },
];

/**
 * The grace period as a quantity and a unit. Stored as minutes whatever the
 * unit (the API has one field); a stored value re-opens in the coarsest unit
 * that divides it, so "2 days" comes back as 2 days and not 2880 minutes.
 * The unit is local state only so that switching it does not reinterpret
 * the quantity behind the author's back: 30 minutes -> hours reads 30 hours.
 */
function GracePeriod({ minutes, disabled = false, onChange }) {
	const stored = useMemo(() => splitLockMinutes(minutes), [minutes]);
	const [unit, setUnit] = useState(stored.unit);
	const [qty, setQty] = useState(stored.qty);
	// A value arriving from outside (another author's save, a reset) re-splits.
	useEffect(() => {
		if (lockMinutesFrom(qty, unit) !== lockMinutesFrom(stored.qty, stored.unit)) {
			setUnit(stored.unit);
			setQty(stored.qty);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stored.qty, stored.unit]);

	const commit = (nextQty, nextUnit) => {
		const safeQty = Math.min(Math.max(1, Math.round(Number(nextQty) || 1)), lockQtyMax(nextUnit));
		setQty(safeQty);
		setUnit(nextUnit);
		onChange(lockMinutesFrom(safeQty, nextUnit));
	};

	return (
		<Space.Compact>
			<InputNumber
				min={1}
				max={lockQtyMax(unit)}
				value={qty}
				aria-label='Grace period'
				disabled={disabled}
				style={{ width: 96 }}
				onChange={value => commit(value, unit)}
			/>
			<Select
				aria-label='Grace period unit'
				value={unit}
				options={LOCK_UNIT_OPTIONS}
				disabled={disabled}
				style={{ width: 120 }}
				onChange={value => commit(qty, value)}
			/>
		</Space.Compact>
	);
}

export default function RecordOptions({ record, onChange, disabled = false }) {

	const config = record && typeof record === 'object' ? record : null;
	const locks = !!(config?.lock?.after && config.lock.after !== 'never');

	const patchLock = next => onChange({ ...(config || {}), lock: { ...(config?.lock || {}), ...next } });

	return (<>
		<ToggleRow
			label='Records of record'
			description={'These items freeze once made: after that they are corrected, not edited. '
				+ 'The correction is a new entry that supersedes the original, and both are kept.'}
			checked={locks}
			disabled={disabled}
			onChange={value => onChange(value
				? { lock: { after: 'minutes', minutes: 15 }, delete: 'manage-with-reason' }
				: false)}
		/>

		{locks ? (
			<div className='toggle-row--child'>
				<Space direction='vertical' size={12} style={{ width: '100%' }}>
					<div>
						<label htmlFor='recordLockAfter' className='d-block'>Locks</label>
						<Space wrap align='start'>
							<Select
								id='recordLockAfter'
								style={{ minWidth: 220 }}
								value={config.lock.after}
								options={AFTER_OPTIONS}
								disabled={disabled}
								onChange={value => patchLock({ after: value, ...(value === 'minutes' && !config.lock.minutes ? { minutes: 15 } : {}) })}
							/>
							{config.lock.after === 'minutes' ? (
								<GracePeriod
									minutes={config.lock.minutes ?? 15}
									disabled={disabled}
									onChange={minutes => patchLock({ minutes })}
								/>
							) : null}
						</Space>
						<Text type='secondary' className='d-block'>
							A manager can reopen a locked record with a reason; the unlock closes again on the next save.
							Driven fields and automations keep writing — a lock is about a person changing what they wrote.
						</Text>
					</div>

					<div>
						<label htmlFor='recordDelete' className='d-block'>Deleting one</label>
						<Select
							id='recordDelete'
							style={{ width: '100%' }}
							value={config.delete || 'manage-with-reason'}
							options={DELETE_OPTIONS}
							disabled={disabled}
							onChange={value => onChange({ ...config, delete: value })}
						/>
						<Text type='secondary'>
							A delete that walks around the lock would make the lock decorative. The reason is kept either way.
						</Text>
					</div>
				</Space>
			</div>
		) : null}
	</>);
}
